import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import Input from '../components/ui/Input';
import './Auth.css';

/* ─────────────────────────────────────────────────────────────
   RESET PASSWORD PAGE

   Users arrive here by clicking the link in the reset email.
   Supabase embeds a one-time token in the URL; the Supabase
   client picks it up automatically and fires a PASSWORD_RECOVERY
   auth event. We wait for that event before showing the form —
   if we showed the form immediately the session might not exist
   yet and updateUser() would fail.
   ───────────────────────────────────────────────────────────── */
function ResetPassword() {
  const navigate = useNavigate();

  // 'verifying' → waiting for Supabase to process the token
  // 'ready'     → PASSWORD_RECOVERY event received, show form
  // 'done'      → password updated successfully
  const [status, setStatus]   = useState('verifying');
  const [newPassword, setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError]     = useState('');
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    // Listen for the PASSWORD_RECOVERY event that Supabase fires
    // after it validates the token from the email link.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setStatus('ready');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSaving(true);
    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    setSaving(false);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    setStatus('done');

    // Sign out the temporary recovery session, then redirect to login
    await supabase.auth.signOut();
    setTimeout(() => navigate('/login'), 2000);
  }

  return (
    <div className="auth-page">
      <div className="auth-card">

        <Link to="/" className="auth-logo">
          Rank<span>TT</span>
        </Link>

        {status === 'verifying' && (
          <>
            <h1 className="auth-title">Verifying link…</h1>
            <p className="auth-subtitle" style={{ textAlign: 'center' }}>
              Please wait while we verify your reset link.
            </p>
          </>
        )}

        {status === 'ready' && (
          <>
            <h1 className="auth-title">New password</h1>
            <p className="auth-subtitle">Choose a strong password for your account.</p>

            <form className="auth-form" onSubmit={handleSubmit} noValidate>

              <FormField label="New password" htmlFor="new-password" error={error}>
                <Input
                  id="new-password"
                  name="new-password"
                  type="password"
                  placeholder="At least 6 characters"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  error={error}
                />
              </FormField>

              <FormField label="Confirm new password" htmlFor="confirm-password">
                <Input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  placeholder="Repeat your new password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </FormField>

              {error && <p className="auth-error">{error}</p>}

              <Button type="submit" fullWidth size="lg" disabled={saving}>
                {saving ? 'Saving…' : 'Update password'}
              </Button>

            </form>
          </>
        )}

        {status === 'done' && (
          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            <p style={{ color: 'var(--accent)', fontWeight: 700, fontSize: '15px', marginBottom: '8px' }}>
              Password updated!
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
              Redirecting you to log in…
            </p>
          </div>
        )}

      </div>
    </div>
  );
}

export default ResetPassword;
